import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { Button, Spin, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import * as XLSX from 'xlsx';

import formatDateWithTimezone from "../../../utils/dateUtils";
import { fetchWebOrdersWithoutStripe } from '../../../core/api/data';

const WebOrdersWithoutStripeModal = ({
  visible,
  onClose,
  filters,
  timezone,
  merchantName
}) => {
  const [webOrdersData, setWebOrdersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const gridRef = useRef(null);

  // Column definitions for web orders without Stripe transactions
  const columnDefs = [
    { headerName: "#", field: "index", width: 70 },
    { headerName: "Order ID", field: "id", width: 120 },
    { headerName: "Order Number", field: "order_number", width: 150 },
    { 
      headerName: "Customer Name", 
      valueGetter: p => { 
        const f = p.data.billing?.first_name || ""; 
        const l = p.data.billing?.last_name || ""; 
        return `${f} ${l}`.trim(); 
      }, 
      width: 200 
    },
    { headerName: "Customer Email", valueGetter: p => p.data.billing?.email || "", width: 200 },
    { headerName: "Total Amount", field: "total", width: 120 },
    { headerName: "Date Created", field: "date_created", width: 150 },
    { headerName: "Date Completed", field: "date_completed", width: 150 },
    { headerName: "Date Paid", field: "date_paid", width: 150 },
    { headerName: "Payment Method", field: "payment_method", width: 150 },
    { headerName: "Payment Method Title", field: "payment_method_title", width: 200 },
    { headerName: "Status", field: "status", width: 120 },
  ];

  // Fetch data when the modal becomes visible
  useEffect(() => {
    if (visible && merchantName && filters?.brands?.length > 0) {
      fetchData();
    }
  }, [visible, merchantName, filters]);

  // Fetch web orders without Stripe transactions
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchWebOrdersWithoutStripe(filters, merchantName);
      
      const formattedData = response.map((order, index) => {
        return {
          ...order,
          index: index + 1,
          date_created: order.date_created ? formatDateWithTimezone(order.date_created, timezone) : '',
          date_completed: order.date_completed ? formatDateWithTimezone(order.date_completed, timezone) : '',
          date_paid: order.date_paid ? formatDateWithTimezone(order.date_paid, timezone) : '',
          total: order.total ? `$${parseFloat(order.total).toFixed(2)}` : '$0.00',
        };
      });

      setWebOrdersData(formattedData);
    } catch (error) {
      console.error('Error fetching web orders without Stripe transactions:', error);
      message.error('Failed to fetch web orders without Stripe transactions.');
    } finally {
      setLoading(false);
    }
  };

  // Export data to Excel
  const exportToExcel = () => {
    try {
      if (webOrdersData.length === 0) {
        message.warning('No data to export');
        return;
      }

      // Prepare data for export
      const exportData = webOrdersData.map(order => {
        const billingInfo = order.billing || {};
        
        return {
          'Order ID': order.id,
          'Order Number': order.order_number,
          'Customer Name': `${billingInfo.first_name || ''} ${billingInfo.last_name || ''}`.trim(),
          'Customer Email': billingInfo.email || '',
          'Total Amount': order.total,
          'Date Created': order.date_created,
          'Date Completed': order.date_completed,
          'Date Paid': order.date_paid,
          'Payment Method': order.payment_method,
          'Payment Method Title': order.payment_method_title,
          'Status': order.status,
        };
      });
      
      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Web Orders Without Stripe');
      
      // Generate filename with merchant name and date
      const filename = `Web_Orders_Without_Stripe_${merchantName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      
      // Save file
      XLSX.writeFile(wb, filename);
      message.success(`Exported to ${filename}`);
    } catch (error) {
      console.error("Error exporting data:", error);
      message.error("Failed to export data");
    }
  };

  return (
    <Modal
      show={visible}
      onHide={onClose}
      size="xl"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Web Orders Without Stripe Transactions for {merchantName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="p-3">
          <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '10px'}}>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              disabled={loading || !webOrdersData.length}
            >
              Export to Excel
            </Button>
          </div>
          
          <div className="ag-theme-alpine" style={{ width: "100%", height: "500px", position: 'relative' }}>
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 1000,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Spin size="large" />
                <div style={{ marginTop: '15px', fontWeight: 'bold' }}>Loading web orders...</div>
              </div>
            )}
            <AgGridReact
              ref={gridRef}
              rowData={webOrdersData}
              columnDefs={columnDefs}
              pagination={true}
              paginationPageSize={100}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
              }}
              onGridReady={(params) => {
                gridRef.current = params;
                if (params.api) {
                  params.api.sizeColumnsToFit();
                }
              }}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WebOrdersWithoutStripeModal;
